import { AvatarV2 } from '@cimplico/components';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    LexicalTypeaheadMenuPlugin,
    QueryMatch,
    TypeaheadOption,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';
import { useCallback, useMemo, useState } from 'react';
import { useUsers } from '../../../queries/use-users';
import { $createMentionNode } from '../nodes/MentionNode';
import ReactDOM from 'react-dom';

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
    NAME,
    PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
    '(?:' +
    '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
    ' |' + // E.g. " " in "Josh Duck"
    '[' +
    PUNC +
    ']|' + // E.g. "-' in "Salier-Hellendag"
    ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
    '(^|\\s|\\()(' + '[' + TRIGGERS + ']' + '((?:' + VALID_CHARS + VALID_JOINS + '){0,' + LENGTH_LIMIT + '})' + ')$'
);

// At most, 10 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 10;

function checkForAtSignMentions(text: string, minMatchLength: number): QueryMatch | null {
    const match = AtSignMentionsRegex.exec(text);

    if (match !== null) {
        // The strategy ignores leading whitespace but we need to know it's
        // length to add it to the leadOffset
        const maybeLeadingWhitespace = match[1];

        const matchingString = match[3];
        if (matchingString.length >= minMatchLength) {
            return {
                leadOffset: match.index + maybeLeadingWhitespace.length,
                matchingString,
                replaceableString: match[2],
            };
        }
    }
    return null;
}

function getPossibleQueryMatch(text: string): QueryMatch | null {
    return checkForAtSignMentions(text, 0);
}

class MentionTypeaheadOption extends TypeaheadOption {
    id: string;
    name: string;
    picture: JSX.Element;

    constructor(id: string, name: string, picture: JSX.Element) {
        super(id);
        this.name = name;
        this.picture = picture;
        this.id = id;
    }
}

function MentionsTypeaheadMenuItem({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
}: {
    index: number;
    isSelected: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    option: MentionTypeaheadOption;
}): JSX.Element {
    let className = 'p-2 cursor-pointer bg-white hover:bg-gray-100 flex items-center shrink-0';

    if (isSelected) {
        className += ' bg-gray-100';
    }

    return (
        <li
            key={option.key}
            tabIndex={-1}
            className={className}
            ref={option.setRefElement}
            role="option"
            aria-selected={isSelected}
            id={'typeahead-item-' + index}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
        >
            {option.picture}
            <span className="text pl-2 text-sm">{option.name}</span>
        </li>
    );
}

export default function NewMentionsPlugin(): JSX.Element | null {
    const [editor] = useLexicalComposerContext();

    const [queryString, setQueryString] = useState<string | null>('');

    const userQuery = useUsers({
        page: 1,
        search: queryString ? `${queryString}&searchFields=name&searchFilters=role` : '',
        sort: '',
        filter: 'role=USER&role=MANAGER&role=ADMIN',
    });

    const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
        minLength: 0,
    });

    const options = useMemo(
        () =>
            userQuery.data?.users
                .map(
                    (result) =>
                        new MentionTypeaheadOption(
                            result.id,
                            `${result.firstName} ${result.lastName}`,
                            (
                                <AvatarV2
                                    src={result.avatarUrl}
                                    size="xs"
                                    alt={result.firstName[0] + result.lastName[0]}
                                    color={result.profileHexColour}
                                />
                            )
                        )
                )
                .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
        [userQuery.data?.users]
    ) as MentionTypeaheadOption[];

    const onSelectOption = useCallback(
        (selectedOption: MentionTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
            editor.update(() => {
                const mentionNode = $createMentionNode(selectedOption.id, selectedOption.name);
                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode);
                }
                mentionNode.select();
                closeMenu();
            });
        },
        [editor]
    );

    const checkForMentionMatch = useCallback(
        (text: string) => {
            const mentionMatch = getPossibleQueryMatch(text);
            const slashMatch = checkForSlashTriggerMatch(text, editor);
            return !slashMatch && mentionMatch ? mentionMatch : null;
        },
        [checkForSlashTriggerMatch, editor]
    );

    return (
        <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={checkForMentionMatch}
            options={options}
            menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }): React.ReactPortal | null =>
                anchorElementRef.current && userQuery.data?.totalItems
                    ? ReactDOM.createPortal(
                          <div className="w-60 max-h-48 border mt-6 overflow-y-auto rounded-lg">
                              <ul className="p-0 m-0 min-w-[180px]">
                                  {options.map((option, i: number) => (
                                      <MentionsTypeaheadMenuItem
                                          index={i}
                                          isSelected={selectedIndex === i}
                                          onClick={(): void => {
                                              setHighlightedIndex(i);
                                              selectOptionAndCleanUp(option);
                                          }}
                                          onMouseEnter={(): void => {
                                              setHighlightedIndex(i);
                                          }}
                                          key={option.key}
                                          option={option}
                                      />
                                  ))}
                              </ul>
                          </div>,
                          anchorElementRef.current
                      )
                    : null
            }
        />
    );
}
